import { prepareProductImageUpload } from './product-image-upload';

export type JsonRecord = Record<string, unknown>;

export interface AdminCredentials {
  appId: string;
  token: string;
}

export interface AdminCaptchaChallenge {
  challengeId: string;
  question: string;
  expiresAt: string;
}

export interface AdminLoginSession {
  accessToken: string;
  expiresAt: string;
  user: { id: string; displayName: string };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }

  get isPermissionError() {
    return this.status === 401 || this.status === 403;
  }
}

function readError(payload: unknown, status: number): ApiError {
  const body = (payload && typeof payload === 'object' ? payload : {}) as JsonRecord;
  const nested = (body.error && typeof body.error === 'object' ? body.error : body) as JsonRecord;
  return new ApiError(
    status,
    String(nested.code ?? `HTTP_${status}`),
    String(nested.message ?? '请求失败，请稍后重试'),
  );
}

function parsePayload(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { message: text };
  }
}

export class AdminApiClient {
  constructor(
    private readonly credentials: () => AdminCredentials,
    private readonly baseUrl = import.meta.env.VITE_API_BASE_URL ?? '',
    private readonly onUnauthorized?: (error: ApiError) => void,
  ) {}

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const { appId, token } = this.credentials();
    if (!appId || !token) {
      throw new ApiError(401, 'ADMIN_CREDENTIALS_REQUIRED', '请先配置后台认证 App ID 与访问令牌');
    }
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-app-id': appId,
        Authorization: `Bearer ${token}`,
        ...init.headers,
      },
    });
    const text = await response.text();
    const payload = parsePayload(text);
    if (!response.ok) {
      const error = readError(payload, response.status);
      if (response.status === 401) this.onUnauthorized?.(error);
      throw error;
    }
    return payload as T;
  }

  async getAdminCaptcha(appId: string): Promise<AdminCaptchaChallenge> {
    if (!appId) throw new ApiError(400, 'ADMIN_APP_CONTEXT_REQUIRED', '后台应用标识尚未配置');
    const response = await fetch(`${this.baseUrl}/api/admin/auth/captcha`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'x-app-id': appId,
      },
      cache: 'no-store',
    });
    const text = await response.text();
    const payload = parsePayload(text);
    if (!response.ok) throw readError(payload, response.status);
    return payload as AdminCaptchaChallenge;
  }

  async loginAdmin(
    appId: string,
    username: string,
    password: string,
    captchaChallengeId: string,
    captchaAnswer: string,
  ): Promise<AdminLoginSession> {
    return this.publicAdminAuthRequest('/api/admin/auth/login', appId, {
      username,
      password,
      captchaChallengeId,
      captchaAnswer,
    });
  }

  async logout(): Promise<void> {
    await this.request('/api/admin/auth/logout', { method: 'POST' });
  }

  private async publicAdminAuthRequest<T>(path: string, appId: string, body: unknown): Promise<T> {
    if (!appId) throw new ApiError(400, 'ADMIN_APP_CONTEXT_REQUIRED', '后台应用标识尚未配置');
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-app-id': appId,
      },
      body: JSON.stringify(body),
    });
    const text = await response.text();
    const payload = parsePayload(text);
    if (!response.ok) {
      const error = readError(payload, response.status);
      if (response.status === 401) this.onUnauthorized?.(error);
      throw error;
    }
    return payload as T;
  }

  get<T>(path: string) {
    return this.request<T>(path);
  }

  send<T>(path: string, method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', body?: unknown) {
    return this.request<T>(path, {
      method,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  }

  async upload<T>(path: string, form: FormData, idempotencyKey: string): Promise<T> {
    const { appId, token } = this.credentials();
    if (!appId || !token)
      throw new ApiError(401, 'ADMIN_CREDENTIALS_REQUIRED', '请先配置后台认证 App ID 与访问令牌');
    const preparedForm = await prepareProductImageUpload(path, form);
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'x-app-id': appId,
        Authorization: `Bearer ${token}`,
        'idempotency-key': idempotencyKey,
      },
      body: preparedForm,
    });
    const text = await response.text();
    const payload = parsePayload(text);
    if (!response.ok) {
      const error = readError(payload, response.status);
      if (response.status === 401) this.onUnauthorized?.(error);
      throw error;
    }
    return payload as T;
  }

  async downloadWithMetadata(
    path: string,
    extraHeaders: Record<string, string> = {},
    signal?: AbortSignal,
  ): Promise<{ blob: Blob; headers: Headers }> {
    const { appId, token } = this.credentials();
    if (!appId || !token)
      throw new ApiError(401, 'ADMIN_CREDENTIALS_REQUIRED', '请先配置后台认证 App ID 与访问令牌');
    const response = await fetch(`${this.baseUrl}${path}`, {
      headers: { 'x-app-id': appId, Authorization: `Bearer ${token}`, ...extraHeaders },
      cache: 'no-store',
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) {
      const payload = parsePayload(await response.text());
      const error = readError(payload, response.status);
      if (response.status === 401) this.onUnauthorized?.(error);
      throw error;
    }
    return { blob: await response.blob(), headers: response.headers };
  }

  async download(
    path: string,
    extraHeaders: Record<string, string> = {},
    signal?: AbortSignal,
  ): Promise<Blob> {
    return (await this.downloadWithMetadata(path, extraHeaders, signal)).blob;
  }
}

export function unwrapList(value: unknown): JsonRecord[] {
  if (Array.isArray(value)) return value.filter(isRecord);
  if (!isRecord(value)) return [];
  for (const key of [
    'items',
    'data',
    'cities',
    'apps',
    'products',
    'pages',
    'templates',
    'definitions',
    'versions',
  ]) {
    const candidate = value[key];
    if (Array.isArray(candidate)) return candidate.filter(isRecord);
  }
  return [];
}

export function unwrapRecord(value: unknown): JsonRecord {
  if (!isRecord(value)) return {};
  if (isRecord(value.data)) return value.data;
  return value;
}

export function isRecord(value: unknown): value is JsonRecord {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function textOf(record: JsonRecord, ...keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
      return String(value);
  }
  return '—';
}
